import { useTranslations } from "use-intl";
import { Chip, Divider, Avatar, Button, CircularProgress } from "@mui/material";
import { AlarmIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import type { FindFlightOffersFlightStopV2 } from "@/app/lib/types/codegen";
import TravellersListSummary from "./TravellersListSummary";

import { ReservationDocumentList } from "../ReservationDocumentList";
import { type SingleFightDetailsData } from "@/app/lib/context/SingleFlightDetailsContext";
import FlightArrow from "../FlightArrow";
import { FlightInfo } from "../flights/FlightInfo";
import { useOrder } from "@/app/lib/hooks/useOrder";
import { type Traveller } from "@/app/lib/types/types";
import { parseDuration } from "@/app/lib/utils/duration";
import type { FlightType } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";

function FlightBookSuccess({
  departureStops,
  selectedTravellers,
  departureFlight,
  returnStops,
  returnFlight,
  singleFlightDetails,
  orderId,
}: {
  departureStops: FindFlightOffersFlightStopV2[];
  selectedTravellers: Traveller[];
  returnStops: FindFlightOffersFlightStopV2[];
  departureFlight?: FlightType;
  returnFlight?: FlightType;
  singleFlightDetails?: SingleFightDetailsData;
  orderId: string;
}) {
  const t = useTranslations();
  const navigate = useNavigate();

  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);

  const isLoading = isOrderLoading || !orderData?.reservations.length;

  const baggage =
    singleFlightDetails?.optionalServices?.filter(
      (service) =>
        service.included && ["BG", "CY"].includes(service.service.categoryCode),
    ) || [];

  return (
    <div className="flex w-full flex-col items-center gap-4 px-4">
      <h5 className="mt-6 mb-10 text-xl font-semibold text-black">
        {t("flights.flightBooked")}
      </h5>
      <div className="section-card flex w-full items-center justify-between gap-4">
        <span className="section-title mb-0">{t("flights.ticketNumber")}</span>
        <h3 className="text-base font-semibold text-text-title">
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            orderData.reservations[0].reservationId
          )}
        </h3>
      </div>

      {orderData?.reservations.map((reservation) => (
        <ReservationDocumentList
          key={reservation.id}
          reservationId={reservation.reservationId}
        />
      ))}

      <div className="section-card flex w-full flex-col gap-4">
        {departureFlight && (
          <span className="section-title m-0">{t("flights.departure")}</span>
        )}
        {departureFlight &&
          departureFlight.segments.map((segment, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-2"
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={t("flights.airlineLogoAlt", {
                      airline: segment.marketingCarrier.airline.name,
                    })}
                    sx={{
                      width: 16,
                      height: 16,
                      fontSize: "12px",
                    }}
                    src={segment.marketingCarrier.airline.logoUrl}
                  >
                    {segment.marketingCarrier.airline.name?.split("")[0]}
                  </Avatar>
                  <span className="text-xs text-text-primary">
                    {segment.marketingCarrier.airline.name ||
                      t("unknownAirline")}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {segment.equipment.name || segment.equipment.code}{" "}
                  {segment.marketingCarrier.flightNumber}
                </span>
              </div>

              <FlightInfo
                origin={{
                  date: segment.departure.date,
                  time: segment.departure.time,
                  code: segment.departure.airport.code,
                }}
                destination={{
                  date: segment.arrival.date,
                  time: segment.arrival.time,
                  code: segment.arrival.airport.code,
                }}
              >
                <FlightArrow overallTime={segment.duration} />
              </FlightInfo>

              {departureStops.length > 0 && departureStops[index]?.stopTime && (
                <Chip
                  icon={<AlarmIcon size={16} />}
                  className="mx-auto"
                  color="primary"
                  label={t("flights.flightStopWithTime", {
                    ...parseDuration(departureStops[index].stopTime),
                    cityName: segment.arrival.city.name,
                    code: segment.arrival.airport.code,
                  })}
                />
              )}
            </div>
          ))}
        {returnFlight && (
          <span className="section-title m-0">
            {t("flights.search.return")}
          </span>
        )}
        {returnFlight &&
          returnFlight.segments.map((segment, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-4"
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={t("flights.airlineLogoAlt", {
                      airline: segment.marketingCarrier.airline.name,
                    })}
                    sx={{
                      width: 16,
                      height: 16,
                      fontSize: "12px",
                    }}
                    src={segment.marketingCarrier.airline.logoUrl}
                  >
                    {segment.marketingCarrier.airline.name?.split("")[0]}
                  </Avatar>
                  <span className="text-xs text-text-primary">
                    {segment.marketingCarrier.airline.name ||
                      t("unknownAirline")}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {segment.equipment.name || segment.equipment.code}{" "}
                  {segment.marketingCarrier.flightNumber}
                </span>
              </div>

              <FlightInfo
                origin={{
                  date: segment.departure.date,
                  time: segment.departure.time,
                  code: segment.departure.airport.code,
                }}
                destination={{
                  date: segment.arrival.date,
                  time: segment.arrival.time,
                  code: segment.arrival.airport.code,
                }}
              >
                <FlightArrow overallTime={segment.duration} />
              </FlightInfo>

              {returnStops.length > 0 && returnStops[index]?.stopTime && (
                <Chip
                  icon={<AlarmIcon size={16} />}
                  className="mx-auto"
                  color="primary"
                  label={t("flights.flightStopWithTime", {
                    ...parseDuration(returnStops[index].stopTime),
                    cityName: segment.arrival.city.name,
                    code: segment.arrival.airport.code,
                  })}
                />
              )}
            </div>
          ))}
        <Divider sx={{ margin: "1.5rem 0" }} />
        <TravellersListSummary
          selectedTravellers={selectedTravellers}
          baggage={baggage}
        />
      </div>

      <div>
        <div className="section-title">{t("reservations.whatsNext")}</div>
        <div className="section-card">
          <div className="flex flex-col gap-2">
            <h5 className="text-sm font-semibold text-text-primary">
              {t("flights.nextSteps")}
            </h5>
            <span className="block text-sm font-normal text-text-primary">
              {t("flights.nextStepsDescription")}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-auto mb-40 flex flex-col gap-2">
        <Button
          onClick={() => {
            navigate({ to: "/", search: { category: "flights" } });
          }}
          variant="contained"
          size="small"
          sx={{
            width: "240px",
            marginTop: "24px",
          }}
        >
          {t("backToHome")}
        </Button>
        <Button
          onClick={() => {
            navigate({ to: "/bookings", search: { category: "flights" } });
          }}
          variant="text"
          size="small"
          sx={{
            width: "240px",
          }}
        >
          {t("openMyBookings")}
        </Button>
      </div>
    </div>
  );
}

export default FlightBookSuccess;
